import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

interface ColorToken {
  name: string;
  value: string;
  cssVar: string;
}

@Component({
  selector: 'ld-style-guide-page',
  templateUrl: './style-guide-page.component.html',
  styleUrl: './style-guide-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideNativeDateAdapter()],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatDatepickerModule,
    MatTabsModule,
    MatChipsModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
  ],
})
export class StyleGuidePageComponent {
  readonly primaryColors: ColorToken[] = [
    { name: 'Primary', value: '#501283', cssVar: '--color-ld-primary' },
    { name: 'Primary 50', value: '#fcebff', cssVar: '--color-ld-primary-50' },
    { name: 'Primary 100', value: '#f5d9ff', cssVar: '--color-ld-primary-100' },
    { name: 'Primary 500', value: '#7e469d', cssVar: '--color-ld-primary-500' },
    { name: 'Primary 700', value: '#642d84', cssVar: '--color-ld-primary-700' },
    { name: 'Primary 900', value: '#30004a', cssVar: '--color-ld-primary-900' },
  ];

  readonly semanticColors: ColorToken[] = [
    { name: 'Link', value: '#0d6bb8', cssVar: '--color-ld-link' },
    { name: 'Link Hover', value: '#0a5a9e', cssVar: '--color-ld-link-hover' },
    { name: 'Success', value: '#05a678', cssVar: '--color-ld-success' },
    { name: 'Error', value: '#cf4452', cssVar: '--color-ld-error' },
    { name: 'Error Light', value: '#fdf6f6', cssVar: '--color-ld-error-light' },
    { name: 'Warning', value: '#f59e0b', cssVar: '--color-ld-warning' },
    { name: 'Warning 50', value: '#fffbf2', cssVar: '--color-ld-warning-50' },
  ];

  readonly neutralColors: ColorToken[] = [
    { name: 'Neutral', value: '#2b2a35', cssVar: '--color-ld-neutral' },
    { name: 'Neutral 50', value: '#f5f5f5', cssVar: '--color-ld-neutral-50' },
    { name: 'Neutral 100', value: '#f4f4f6', cssVar: '--color-ld-neutral-100' },
    { name: 'Neutral 200', value: '#c0bfcb', cssVar: '--color-ld-neutral-200' },
    { name: 'Neutral 400', value: '#9391a1', cssVar: '--color-ld-neutral-400' },
    { name: 'Neutral 500', value: '#575560', cssVar: '--color-ld-neutral-500' },
    { name: 'Neutral 700', value: '#222222', cssVar: '--color-ld-neutral-700' },
    { name: 'Border', value: '#e6e5ea', cssVar: '--color-ld-border' },
  ];

  readonly accentColors: ColorToken[] = [
    { name: 'Jade', value: '#05a678', cssVar: '--color-ld-jade' },
    { name: 'Jade 50', value: '#f3fbf8', cssVar: '--color-ld-jade-50' },
    { name: 'Jade 100', value: '#cdede4', cssVar: '--color-ld-jade-100' },
    { name: 'Jade 600', value: '#036448', cssVar: '--color-ld-jade-600' },
    { name: 'Lime', value: '#afe321', cssVar: '--color-ld-lime' },
    { name: 'Lime Dark', value: '#647c22', cssVar: '--color-ld-lime-dark' },
    { name: 'Teal', value: '#00d4aa', cssVar: '--color-ld-teal' },
    { name: 'Trustpilot', value: '#00b67a', cssVar: '--color-trustpilot' },
  ];
}
